import type { ActorInfoApi } from "../api/Users";
import type { ReportSummaryItemApi } from "../api/Reports";
import type { WorkflowRun, WorkflowRunRowDataResponse } from "../api/Workflows";
import { isWorkflowRunVisuallyLocked } from "../api/Workflows";
import type { ReportUpdateFn } from "../services/websocket/reportCacheUpdates";

/** 360 list rows and claim/unlock should target the ``360`` sub-workflow run, not the parent. */
export function get360SubworkflowRunUuid(
  subworkflowData: WorkflowRun[] | undefined
): string | undefined {
  if (!subworkflowData?.length) {
    return undefined;
  }
  const match = subworkflowData.find((wr) => wr.name === "360");
  const id = match?.external_id?.trim();
  return id || undefined;
}

/**
 * Sub-workflow runs that belong to ``parentWorkflowRunUuid``, or undefined when the loaded batch was
 * fetched for a different parent. Callers filter here instead of clearing the fetch when the report
 * changes, so a response that lands during an unrelated re-render is not discarded.
 */
export function selectRunsForParent(
  runs: WorkflowRun[] | undefined,
  fetchedParentUuid: string | undefined,
  parentWorkflowRunUuid: string | undefined
): WorkflowRun[] | undefined {
  if (!runs?.length || !parentWorkflowRunUuid) {
    return undefined;
  }
  return fetchedParentUuid === parentWorkflowRunUuid ? runs : undefined;
}

/** Whether the report has no assignee (360 lists use display name; summary also has actor uuid). */
export function isReportUnassigned(
  report: Pick<ReportSummaryItemApi, "locked_by" | "locked_by_name">
): boolean {
  const displayName = (report.locked_by_name ?? "").trim();
  const lockedBy = (report.locked_by ?? "").trim();
  return displayName.length === 0 && lockedBy.length === 0;
}

/**
 * Whether the 360 sub-workflow has no assignee. Requires loaded row data; callers
 * must not treat a missing row as unassigned.
 */
export function is360WorkflowRunUnassigned(
  row: Pick<
    WorkflowRunRowDataResponse,
    "is_locked" | "locked_by" | "lock_expire"
  >
): boolean {
  return !isWorkflowRunVisuallyLocked(row);
}

export function getActorDisplayName(
  actorInfo: ActorInfoApi | undefined
): string {
  const actorName = (actorInfo?.actorName ?? "").trim();
  if (actorName) return actorName;
  const name = (actorInfo?.name ?? "").trim();
  if (name) return name;
  return (actorInfo?.email ?? "").trim();
}

export function buildClaimedReportUpdate(
  actorUuid: string | undefined,
  actorDisplayName: string
): ReportUpdateFn {
  return (report) => ({
    ...report,
    is_locked: true,
    locked_by: actorUuid ?? report.locked_by,
    locked_by_name: actorDisplayName,
  });
}
